import React from 'react';
import { Box, Typography } from '@mui/material';
import ProductThumb from './ProductThumb';
import { SeasonBadge } from './Badges';
import { displayTitle, thumbTarget } from './format';
import { BLOCKED_NOTE, blockedPieces, blockedRows } from './orderPlan';
import { C, MONO, RADIUS } from './tokens';
import { RestockV2Blocked, RestockV2Order, RestockV2ThumbTarget } from './types';

// 每個顏色都不能訂. Magnus 2026-09-18, SKUs 127 and 2814 on prod: a style he APPROVED, with a
// target and nothing at the counter, whose every needed colour is blocked, ends up with
// order_qty 0 — so it is filtered out of `order.groups`, which is what this screen renders. It
// was in the payload and on no screen at all. Same failure class as the 選品 gate, and the same
// answer: a style may leave the ORDER, never the SCREEN.
//
// Like 沒有登記進貨店家, these rows are NOT tickable and NOT in the copied order — nobody can
// order them. The section exists to say what is missing and why, so the block can be fixed.
const BlockedRow: React.FC<{ row: RestockV2Blocked; onImage: (target: RestockV2ThumbTarget) => void }> = ({
  row, onImage,
}) => (
  <Box
    component="article" data-blocked={row.sku}
    sx={{
      bgcolor: C.card, border: '1px dashed', borderColor: C.line2, borderRadius: `${RADIUS}px`,
      p: 1.1, mb: 0.9,
    }}
  >
    <Box sx={{ display: 'flex', gap: 1.25 }}>
      <ProductThumb
        target={thumbTarget({ sku: row.sku, title: row.title || row.sku, image_url: row.image_url ?? null })}
        width={64} height={85} radius={7} onOpen={onImage}
      />
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: '4px' }}>
          <SeasonBadge value={row.season} />
          <Typography component="span" sx={{ fontSize: 11, color: C.ink3 }}>{row.cat}</Typography>
        </Box>
        <Typography sx={{ fontSize: 14.5, fontWeight: 800, letterSpacing: '-.01em', lineHeight: 1.3 }}>
          {displayTitle(row.title || row.sku)}
        </Typography>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: '5px', alignItems: 'center', mt: '2px' }}>
          <Typography component="span" sx={{ fontFamily: MONO, fontSize: 11, color: C.ink3 }}>{row.sku}</Typography>
          {!!row.source_tag && (
            <Typography component="span" sx={{ fontSize: 11, color: C.ink3 }}>{row.source_tag}</Typography>
          )}
        </Box>
        <Typography sx={{ fontSize: 12, color: C.back, fontWeight: 700, mt: '3px' }}>{row.reason}</Typography>
        {!!row.why && <Typography sx={{ fontSize: 11.5, color: C.ink3 }}>{row.why}</Typography>}
      </Box>
      <Box aria-label={`要訂 ${row.order_qty_would_be} 件`} sx={{ textAlign: 'right', flex: 'none' }}>
        <Typography sx={{ fontSize: 10.5, color: C.ink3, fontWeight: 700 }}>要訂</Typography>
        <Typography sx={{ fontFamily: MONO, fontSize: 22, fontWeight: 700, letterSpacing: '-.03em', lineHeight: 1.1 }}>
          {row.order_qty_would_be}
        </Typography>
        <Typography sx={{ fontSize: 10.5, color: C.ink3, fontWeight: 700 }}>件</Typography>
      </Box>
    </Box>
  </Box>
);

const BlockedSection: React.FC<{
  order: RestockV2Order | undefined; onImage: (target: RestockV2ThumbTarget) => void;
}> = ({ order, onImage }) => {
  const rows = blockedRows(order);
  if (!rows.length) return null;
  const pieces = blockedPieces(order);
  return (
    <Box component="section" aria-label={`每個顏色都不能訂 ${pieces} 件`} data-blocked-section="" sx={{ mt: 2.5 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, px: '2px', pb: 0.5 }}>
        <Typography component="h3" sx={{ fontSize: 14.5, fontWeight: 900, letterSpacing: '-.01em' }}>
          每個顏色都不能訂
          <Box component="span" sx={{ color: C.ink3, fontWeight: 700, fontSize: 12, ml: '5px' }}>
            {rows.length} 款 · 不能下單
          </Box>
        </Typography>
        <Typography
          sx={{
            ml: 'auto', fontFamily: MONO, fontSize: 12, fontWeight: 700, color: C.back,
            bgcolor: C.backWash, borderRadius: '99px', px: 1,
          }}
        >
          {pieces} 件
        </Typography>
      </Box>
      <Typography sx={{ fontSize: 12, color: C.ink2, px: '2px', pb: 0.9 }}>{BLOCKED_NOTE}</Typography>
      {rows.map((row) => <BlockedRow key={row.sku} row={row} onImage={onImage} />)}
    </Box>
  );
};

export default BlockedSection;
